import { CartLinesUpdateEvent } from '@shopify/events';

const cache = new Map();
const PER_PAGE = 3;

function formatMoney(cents, format) {
  const fmt = format || 'LE {{amount}}';
  const value = (Number(cents) || 0) / 100;
  const withSep = (n, d, ts, ds) => {
    const parts = n.toFixed(d).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ts);
    return parts.join(ds);
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return withSep(value, 0, ',', '.');
      case 'amount_with_comma_separator': return withSep(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withSep(value, 0, '.', ',');
      default: return withSep(value, 2, ',', '.');
    }
  });
}

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const imgUrl = (src, w) => {
  if (!src) return '';
  const url = src.startsWith('//') ? 'https:' + src : src;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + w;
};

function zbPrice(c, fmt) { const v = Number(c) || 0, pct = v >= 200000 ? 20 : v >= 100000 ? 15 : 10, now = Math.round(v * (100 - pct) / 100); return '<span class="zb-disc__now">' + formatMoney(now, fmt) + '</span> <s style="opacity:.55;font-size:.85em;font-weight:400">' + formatMoney(v, fmt) + '</s> <span class="zb-disc__tag">-' + pct + '%</span>'; } class ZahabyCartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude'];

  connectedCallback() {
    this.page = 0;
    this.observer = new MutationObserver(() => {
      if (this.products && !this.querySelector('.zs-upsell__inner')) this.render();
    });
    this.observer.observe(this, { childList: true });
    this.load();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  attributeChangedCallback(_n, oldV, newV) {
    if (this.isConnected && oldV !== newV && this.observer) this.load();
  }

  async load() {
    const id = this.dataset.productId;
    if (!id) return;
    const root = (this.dataset.root || '/').replace(/\/?$/, '/');
    try {
      if (!cache.has(id)) {
        cache.set(
          id,
          fetch(`${root}recommendations/products.json?product_id=${id}&limit=10&intent=related`)
            .then((r) => r.json())
            .then((d) => d.products || [])
        );
      }
      const all = await cache.get(id);
      const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
      this.products = all.filter((p) => p.available && !exclude.includes(String(p.id))).slice(0, 9);
      const pages = Math.ceil(this.products.length / PER_PAGE);
      if (this.page >= pages) this.page = 0;
      this.render();
    } catch (e) {
      cache.delete(id);
      this.products = [];
      this.innerHTML = '';
    }
  }

  render() {
    const list = this.products || [];
    if (!list.length) {
      this.innerHTML = '';
      this.hidden = true;
      return;
    }
    this.hidden = false;
    const pages = Math.ceil(list.length / PER_PAGE);
    const slice = list.slice(this.page * PER_PAGE, this.page * PER_PAGE + PER_PAGE);
    const fmt = this.dataset.moneyFormat;
    const addLabel = esc(this.dataset.addLabel || 'Add');

    const cards = slice
      .map((p) => {
        const variants = p.variants.filter((v) => v.available);
        const first = variants[0] || p.variants[0];
        const showSelect = !(p.variants.length === 1 && /default title/i.test(p.variants[0].title));
        const options = variants
          .map((v) => `<option value="${v.id}" data-price="${v.price}">${esc(v.title)}</option>`)
          .join('');
        return `
        <div class="zs-card" data-product="${p.id}">
          <a class="zs-card__media" href="${esc(p.url)}">
            ${p.featured_image ? `<img src="${esc(imgUrl(p.featured_image, 160))}" alt="${esc(p.title)}" loading="lazy" width="80" height="80">` : ''}
          </a>
          <div class="zs-card__info">
            <a class="zs-card__title" href="${esc(p.url)}">${esc(p.title)}</a>
            ${showSelect ? `<span class="zs-card__select-wrap"><select class="zs-card__select" aria-label="${esc(p.title)}">${options}</select></span>` : `<input type="hidden" class="zs-card__select" value="${first.id}">`}
            <span class="zs-card__price">${zbPrice(first.price, fmt)}</span>
          </div>
          <button type="button" class="zs-card__add">
            <span class="zs-card__plus" aria-hidden="true">+</span> ${addLabel}
          </button>
        </div>`;
      })
      .join('');

    const nav =
      pages > 1
        ? `<div class="zs-upsell__nav">
            <button type="button" class="zs-upsell__arrow" data-dir="-1" aria-label="Previous">&#8249;</button>
            <button type="button" class="zs-upsell__arrow" data-dir="1" aria-label="Next">&#8250;</button>
          </div>`
        : '';

    this.innerHTML = `
      <div class="zs-upsell__inner">
        <div class="zs-upsell__head">
          <h3 class="zs-upsell__title">${esc(this.dataset.title)}</h3>
          ${nav}
        </div>
        <div class="zs-upsell__list">${cards}</div>
      </div>`;

    this.querySelectorAll('.zs-upsell__arrow').forEach((b) =>
      b.addEventListener('click', () => {
        this.page = (this.page + Number(b.dataset.dir) + pages) % pages;
        this.render();
      })
    );
    this.querySelectorAll('select.zs-card__select').forEach((s) =>
      s.addEventListener('change', () => {
        const price = s.selectedOptions[0]?.dataset.price;
        s.closest('.zs-card').querySelector('.zs-card__price').innerHTML = zbPrice(price, fmt);
      })
    );
    this.querySelectorAll('.zs-card__add').forEach((b) =>
      b.addEventListener('click', () => this.add(b))
    );
  }

  async add(button) {
    const card = button.closest('.zs-card');
    const variantId = Number(card.querySelector('.zs-card__select').value);
    if (!variantId || button.disabled) return;
    button.disabled = true;
    button.classList.add('is-loading');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId || el.getAttribute('data-section-id'))
      .filter(Boolean);
    if (!sectionIds.includes('cart-drawer-section')) sectionIds.push('cart-drawer-section');

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const root = (this.dataset.root || '/').replace(/\/?$/, '/');
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
      }).then((r) => r.json());

      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'zahaby-cart-upsell',
          itemCount: 1,
          sections: res.sections,
          didError: Boolean(res.status),
        },
      });
    } catch (e) {
      deferred.reject(e);
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('zahaby-cart-upsell')) customElements.define('zahaby-cart-upsell', ZahabyCartUpsell);
